import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api/client";
import { tenantPath } from "../utils/tenantContext";
import "./ObservabilityValidationPanel.css";

async function requestJson(path, options = {}) {
  const response = await apiFetch(tenantPath(path), options);
  let body = {};
  try { body = await response.json(); } catch (_) {}
  if (!response.ok) {
    throw new Error(body.detail || `Request failed (${response.status})`);
  }
  return body;
}

const PROOF_OPTIONS = {
  local: [["local_interface", "Local interface evidence"], ["authorized_manual_evidence", "Authorized manual evidence"]],
  routed: [["route_record", "Routing evidence"], ["authorized_manual_evidence", "Authorized manual evidence"]],
  mirror: [["mirror_configuration", "SPAN / mirror configuration"], ["authorized_manual_evidence", "Authorized manual evidence"]],
  tunnel: [["tunnel_endpoint", "Tunnel endpoint evidence"], ["authorized_manual_evidence", "Authorized manual evidence"]],
};

function ObservabilityValidationPanel() {
  const [devices, setDevices] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [deviceUuid, setDeviceUuid] = useState("");
  const [sensorId, setSensorId] = useState("");
  const [method, setMethod] = useState("routed");
  const [result, setResult] = useState("not_observable");
  const [proofType, setProofType] = useState("route_record");
  const [proofReference, setProofReference] = useState("");
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [lastResult, setLastResult] = useState(null);

  const loadInventory = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([
        requestJson("/network-protection/pcap/targets/devices"),
        requestJson("/network-protection/pcap/targets/sensors"),
      ]);
      const nextDevices = Array.isArray(d.devices) ? d.devices : [];
      const nextSensors = Array.isArray(s.sensors) ? s.sensors : [];
      setDevices(nextDevices);
      setSensors(nextSensors);
      setDeviceUuid((current) => current || nextDevices[0]?.device_uuid || "");
      setSensorId((current) => current || (nextSensors[0]?.id ? String(nextSensors[0].id) : ""));
    } catch (loadError) {
      setError(loadError.message);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    if (!deviceUuid || !sensorId) {
      setHistory([]);
      return;
    }
    try {
      const data = await requestJson(`/network-protection/pcap/targets/validations/${encodeURIComponent(deviceUuid)}?sensor_id=${encodeURIComponent(sensorId)}`);
      setHistory(Array.isArray(data.validations) ? data.validations : []);
    } catch (historyError) {
      setError(historyError.message);
    }
  }, [deviceUuid, sensorId]);

  useEffect(() => { loadInventory(); }, [loadInventory]);
  useEffect(() => { loadHistory(); setLastResult(null); }, [loadHistory]);

  const proofOptions = useMemo(() => PROOF_OPTIONS[method] || [], [method]);
  useEffect(() => { setProofType(proofOptions[0]?.[0] || ""); }, [proofOptions]);

  const validate = async () => {
    if (!deviceUuid || !sensorId || !proofReference.trim()) return;
    const message = result === "verified"
      ? "Record VERIFIED observability for this exact sensor/device relationship? Only do this when you have concrete authorized evidence that target traffic is actually delivered to this sensor."
      : "Record this target as NOT OBSERVABLE by the selected sensor?";
    if (!window.confirm(message)) return;

    try {
      setBusy(true);
      setError("");
      setLastResult(null);
      const response = await requestJson("/network-protection/pcap/targets/validate-observability", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sensor_id: Number(sensorId),
          device_uuid: deviceUuid,
          visibility_method: method,
          result,
          proof_type: proofType,
          proof_reference: proofReference.trim(),
          analyst_notes: notes || null,
        }),
      });
      setLastResult(response);
      setProofReference("");
      setNotes("");
      await loadHistory();
    } catch (validationError) {
      setError(validationError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="observabilityValidationPanel">
      <div className="observabilityValidationHeader">
        <div>
          <span>FINAL VISIBILITY GATE</span>
          <h3>Observability Path Validation</h3>
          <p>VERIFIED visibility requires evidence describing how the selected device traffic is actually delivered to the sensor.</p>
        </div>
        <div className="observabilityValidationBadge">NO CAPTURE</div>
      </div>

      {error ? <div className="observabilityValidationError">{error}</div> : null}

      <div className="observabilityValidationGrid">
        <label>Target Device
          <select value={deviceUuid} disabled={busy} onChange={(e) => setDeviceUuid(e.target.value)}>
            {devices.map((device) => (
              <option key={device.device_uuid} value={device.device_uuid}>
                {device.hostname || device.device_uuid} · {device.platform || "unknown"}
              </option>
            ))}
          </select>
        </label>

        <label>Sensor
          <select value={sensorId} disabled={busy} onChange={(e) => setSensorId(e.target.value)}>
            {sensors.map((sensor) => (
              <option key={sensor.id} value={sensor.id}>
                Sensor {sensor.id} · {sensor.capture_interface || "unknown"}
              </option>
            ))}
          </select>
        </label>

        <label>Visibility Method
          <select value={method} disabled={busy} onChange={(e) => setMethod(e.target.value)}>
            <option value="local">Local</option>
            <option value="routed">Routed</option>
            <option value="mirror">SPAN / Mirror</option>
            <option value="tunnel">Authorized Tunnel</option>
          </select>
        </label>

        <label>Validation Result
          <select value={result} disabled={busy} onChange={(e) => setResult(e.target.value)}>
            <option value="not_observable">Not Observable</option>
            <option value="verified">Verified Observable</option>
          </select>
        </label>

        <label>Proof Type
          <select value={proofType} disabled={busy} onChange={(e) => setProofType(e.target.value)}>
            {proofOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
      </div>

      <label className="observabilityProof">Proof Reference
        <textarea rows={3} value={proofReference} disabled={busy} onChange={(e) => setProofReference(e.target.value)} placeholder="Document route, mirror/SPAN configuration, tunnel endpoint, local interface evidence, ticket/change reference, or other authorized evidence." />
      </label>

      <label className="observabilityProof">Analyst Notes
        <textarea rows={3} value={notes} disabled={busy} onChange={(e) => setNotes(e.target.value)} placeholder="Additional validation context." />
      </label>

      <button type="button" className="observabilityValidateButton" disabled={busy || !deviceUuid || !sensorId || proofReference.trim().length < 8} onClick={validate}>
        Record Observability Validation
      </button>

      {lastResult ? (
        <div className={lastResult.visibility_status === "verified" ? "observabilityResult verified" : "observabilityResult blocked"}>
          <strong>{lastResult.visibility_status?.replaceAll("_", " ").toUpperCase()}</strong>
          <span>Validation ID: {lastResult.validation_id}</span>
        </div>
      ) : null}

      <div className="observabilityHistory">
        <h4>Validation Evidence History</h4>
        {history.length === 0 ? (
          <p>No observability validation evidence has been recorded for this sensor/device pair.</p>
        ) : history.map((entry) => (
          <div className="observabilityHistoryRow" key={entry.id}>
            <strong>{entry.result?.replaceAll("_", " ").toUpperCase()}</strong>
            <span>{entry.visibility_method?.toUpperCase()} · {entry.proof_type}</span>
            <span>{entry.validated_by || "unknown"}</span>
            <span>{entry.validated_at ? new Date(entry.validated_at).toLocaleString() : "—"}</span>
            <code>{entry.proof_reference}</code>
          </div>
        ))}
      </div>

      <div className="observabilityValidationBoundary">
        Recording VERIFIED status does not create network visibility. The referenced route, SPAN/mirror, tunnel, or local collection path must actually exist. This workflow records the evidence used to make that determination. No packet capture is started here.
      </div>
    </section>
  );
}

export default ObservabilityValidationPanel;
